import { formatCurrency, getFormattingLocaleForRegionCode } from 'lib/format/formatCurrencyIntl'
import type { LoyaltyEarnTotals } from 'loyalty/hooks/useGroupedLoyaltyEarnTotals'
import type { OfferPagePriceMode } from 'contexts/OfferPage/offerPageStateReducer'
import type { OfferPageAssistantPriceFormatContext } from './offerPageContextTypes'

const DISPLAY_CONTEXT_FIELD_MAX_LENGTH = 512
const DISPLAY_CONTEXT_BODY_MAX_LENGTH = 4000

type OfferPageSnapshotPriceUnitLabel = 'total' | 'per person'

interface DisplayedPriceSummaryOptions {
  standardTotal?: number
  memberTotal?: number
  valuedUpTo?: number
  showValuedUpTo?: boolean
  /** The assistant's own prose unit label -- see `getOfferPageSnapshotPriceUnitLabel`. */
  unitLabel?: OfferPageSnapshotPriceUnitLabel
}

/**
 * Maps `priceMode` to the assistant's own natural-language unit label. Deliberately NOT the real
 * page's raw `saleUnit` prop values ('total'/'person', see `FlashHotelOptionPriceDetails.tsx`) --
 * those are UI-component inputs that `formatSaleUnitLabel` (`useSaleUnitFormatting.ts`) transforms
 * before they ever reach the screen ('total' renders as no label at all; 'person' renders as a
 * leading-slash "/person"). Copying those literally into prose would silently read "$X person"
 * with no "per". This is a single, centralised mapping so it can't drift between call sites.
 */
export function getOfferPageSnapshotPriceUnitLabel(priceMode: OfferPagePriceMode): OfferPageSnapshotPriceUnitLabel {
  return priceMode === 'perPerson' ? 'per person' : 'total'
}

export function nonEmptyString(value: string | undefined | null): string | undefined {
  const trimmed = value?.trim()
  return trimmed || undefined
}

function boundedNonEmptyString(value: string | undefined | null, maxLength: number): string | undefined {
  const normalized = nonEmptyString(value)
  if (!normalized) {
    return undefined
  }

  return normalized.length > maxLength ? nonEmptyString(normalized.slice(0, maxLength)) : normalized
}

export function displayContextField(value: string | undefined | null): string | undefined {
  return boundedNonEmptyString(value, DISPLAY_CONTEXT_FIELD_MAX_LENGTH)
}

export function displayContextBody(value: string | undefined | null): string | undefined {
  return boundedNonEmptyString(value, DISPLAY_CONTEXT_BODY_MAX_LENGTH)
}

function isFiniteAmount(amount: number | undefined): amount is number {
  return amount !== undefined && Number.isFinite(amount)
}

function formatTotalPrice(
  amount: number,
  { currencyCode, regionCode }: OfferPageAssistantPriceFormatContext,
  unitLabel: OfferPageSnapshotPriceUnitLabel = 'total',
): string {
  return `${formatCurrency(amount, currencyCode, regionCode, { format: 'roundedDollar' })} ${unitLabel}`
}

export function formatEarnNumber(value: number, regionCode: string): string {
  return new Intl.NumberFormat(getFormattingLocaleForRegionCode(regionCode)).format(value)
}

export function formatOfferPageLoyaltyEarnSummary(
  loyaltyEarnTotals: LoyaltyEarnTotals | undefined,
  regionCode: string,
): string | undefined {
  if (!loyaltyEarnTotals || (loyaltyEarnTotals.points <= 0 && loyaltyEarnTotals.statusCredits <= 0)) {
    return undefined
  }

  const summaryParts = loyaltyEarnTotals.points > 0 ?
      [`${formatEarnNumber(loyaltyEarnTotals.points, regionCode)} Société points`] :
      []
  if (loyaltyEarnTotals.statusCredits > 0) {
    summaryParts.push(`${formatEarnNumber(loyaltyEarnTotals.statusCredits, regionCode)} status credits`)
  }

  return `Earn from ${summaryParts.join(' + ')}`
}

export function buildDisplayedPriceSummary(
  {
    standardTotal,
    memberTotal,
    valuedUpTo,
    showValuedUpTo = true,
    unitLabel = 'total',
  }: DisplayedPriceSummaryOptions,
  priceFormatContext: OfferPageAssistantPriceFormatContext,
): string | undefined {
  const priceParts: Array<string> = []
  const payableTotals: Array<number> = []
  const hasStandardTotal = isFiniteAmount(standardTotal)
  const hasMemberTotal = isFiniteAmount(memberTotal) && memberTotal > 0 && memberTotal !== standardTotal

  if (hasMemberTotal) {
    priceParts.push(`LuxPlus+ member ${formatTotalPrice(memberTotal, priceFormatContext, unitLabel)}`)
    payableTotals.push(memberTotal)
  }

  if (hasStandardTotal) {
    const label = hasMemberTotal ? 'non-member ' : ''
    priceParts.push(`${label}${formatTotalPrice(standardTotal, priceFormatContext, unitLabel)}`)
    payableTotals.push(standardTotal)
  }

  const lowestPayableTotal = payableTotals.length ? Math.min(...payableTotals) : undefined
  if (
    showValuedUpTo &&
    isFiniteAmount(valuedUpTo) &&
    valuedUpTo > 0 &&
    (lowestPayableTotal === undefined || valuedUpTo > lowestPayableTotal)
  ) {
    priceParts.push(`valued up to ${formatCurrency(valuedUpTo, priceFormatContext.currencyCode, priceFormatContext.regionCode, { format: 'roundedDollar' })}`)
  }

  return priceParts.length ? priceParts.join('; ') : undefined
}

export function formatPriceSummary(
  amount: number | undefined,
  priceFormatContext: OfferPageAssistantPriceFormatContext,
): string | undefined {
  return buildDisplayedPriceSummary({ standardTotal: amount }, priceFormatContext)
}
